import { Wallet, Plus } from 'lucide-react';
import { useSnackbar } from 'notistack';
import React, { useState } from 'react';

import { useLocationContext } from '../contexts/locationContext';
import { handlePayWithCard } from '../shared/controllers/transactionController';
import { CustomGatewayOption } from '../shared/types';
import { useUserContext } from '../userDashboard/contexts/userContext';

import { formatCurrency } from './currency';
import PinModal from './pinModal';
import { formatUserBalance, PriceableItem } from './price';
import { refreshActiveWalletSnapshot, Profile } from './walletSnapshot';

interface WalletPaymentProps {
    gateway: CustomGatewayOption;
    amount: number | string;
    transactionId: string;
    onSuccess?: (result: {
        success: boolean;
        transaction_id: string;
        payment_method: string;
        pin: string;
        response: unknown;
    }) => void;
    onError?: (error: {
        success: boolean;
        error: string;
        transaction_id: string;
    }) => void;
    userProfile?: Profile | null;
    showAddFunds?: (() => void) | undefined;
}

const WalletPayment: React.FC<WalletPaymentProps> = ({
    gateway,
    amount,
    transactionId,
    onSuccess,
    onError,
    userProfile,
    showAddFunds
}) => {
    const { enqueueSnackbar } = useSnackbar();
    const {
        userProfile: contextUserProfile,
        loadingProfile,
        refreshUserProfile,
        saveUserProfile,
    } = useUserContext();
    const { location: storedLocation } = useLocationContext();

    // Use passed userProfile or fallback to context
    const currentUserProfile = (userProfile || contextUserProfile) as Profile | null;

    const [loading, setLoading] = useState(false);
    const [showPinModal, setShowPinModal] = useState(false);
    const [pin, setPin] = useState(Array(4).fill(''));
    const [error, setError] = useState('');

    const normalizedGatewayCurrency = ((gateway?.currency as string) || '').toString().toUpperCase();
    const gatewayDetailsCurrency = (gateway?.details?.currency as string || '').toString().toUpperCase();
    const walletCurrency =
        normalizedGatewayCurrency ||
        gatewayDetailsCurrency ||
        (currentUserProfile?.wallet?.currency || '').toString().toUpperCase() ||
        (currentUserProfile?.active_currency || '').toString().toUpperCase() ||
        (storedLocation?.countryCode || '').toString().toUpperCase();

    const displayCountryCode =
        (gateway?.details?.country_code as string || '').toString().toUpperCase() ||
        (storedLocation?.countryCode || '').toString().toUpperCase() ||
        undefined;

    const paymentAmount = Number(amount);
    const walletBalance = Number(
        gateway?.details?.balance ??
        currentUserProfile?.wallet?.balance ??
        currentUserProfile?.balance ??
        0
    );

    const currencyMismatch =
        normalizedGatewayCurrency &&
        walletCurrency &&
        normalizedGatewayCurrency !== walletCurrency;

    const handleCodeChange = (newCode: string[]) => {
        setPin([...newCode]);
    };

    const handlePayClick = () => {
        if (currencyMismatch) {
            const errorMsg = `Wallet currency (${walletCurrency || 'N/A'}) does not match payment currency (${normalizedGatewayCurrency}).`;
            setError(errorMsg);
            enqueueSnackbar(errorMsg, { variant: 'warning' });
            return;
        }

        if (walletBalance < paymentAmount) {
            const errorMsg = 'Insufficient wallet balance. Please add funds to your wallet.';
            setError(errorMsg);
            enqueueSnackbar(errorMsg, { variant: 'warning' });
            return;
        }

        setError('');
        setShowPinModal(true);
    };

    const handlePinSubmit = async () => {
        if (pin.some((digit) => digit === '')) {
            enqueueSnackbar('Please enter a complete 4-digit PIN', {
                variant: 'warning',
            });
            return;
        }

        setLoading(true);
        const userData = {
            transaction_id: transactionId,
            payment_method: 'wallet_debit',
            pin: pin.join(''),
        };

        const onPaymentSuccess = async (response: unknown) => {
            setLoading(false);
            setShowPinModal(false);
            const userPin = pin.join('');
            setPin(Array(4).fill(''));

            await refreshActiveWalletSnapshot({
                currency: walletCurrency,
                saveUserProfile,
                userProfile: currentUserProfile || {},
            });
            refreshUserProfile();

            if (onSuccess) {
                onSuccess({
                    success: true,
                    transaction_id: transactionId,
                    payment_method: 'wallet_debit',
                    pin: userPin, // Pass PIN for reconfirmation
                    response
                });
            }
        };

        const onPaymentError = (err: unknown) => {
            setLoading(false);
            setShowPinModal(false);
            setPin(Array(4).fill(''));

            if (onError) {
                const errorObj = err as { message?: string };
                onError({
                    success: false,
                    error: errorObj?.message || 'Wallet payment failed',
                    transaction_id: transactionId
                });
            }
        };

        handlePayWithCard(userData, onPaymentSuccess, onPaymentError);
    };

    const handlePinCancel = () => {
        setShowPinModal(false);
        setPin(Array(4).fill(''));
        setError('');
    };

    const insufficientBalance = walletBalance < paymentAmount;

    const formatAmount = (value: number) => {
        if (walletCurrency) {
            const config: { currencyCode: string; countryCode?: string } = {
                currencyCode: walletCurrency,
            };
            if (displayCountryCode) {
                config.countryCode = displayCountryCode;
            }
            return formatCurrency(value, config);
        }

        return formatUserBalance(
            value,
            currentUserProfile as PriceableItem,
            storedLocation?.countryCode
        );
    };

    return (
        <>
            {showPinModal && (
                <PinModal
                    title="Enter Payment PIN"
                    description="Enter your 4-digit PIN to confirm the wallet payment."
                    isVisible={showPinModal}
                    code={pin}
                    onCodeChange={handleCodeChange}
                    onSubmit={handlePinSubmit}
                    onCancel={handlePinCancel}
                    loading={loading}
                />
            )}

            <div className="space-y-4">
                {/* Wallet Balance Display */}
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                            <Wallet className="w-5 h-5 text-green-600" />
                        </div>
                        <div className="flex-1">
                            <h4 className="font-medium text-green-900">Wallet Balance</h4>
                            <p className="text-2xl font-bold text-green-800">
                                {loadingProfile ? (
                                    <span className="animate-pulse bg-green-200 rounded w-24 h-8 inline-block"></span>
                                ) : (
                                    formatAmount(walletBalance)
                                )}
                            </p>
                            {walletCurrency && (
                                <p className="text-xs text-green-700 mt-1 uppercase tracking-wide">
                                    Currency: {walletCurrency}
                                </p>
                            )}
                        </div>
                    </div>

                    {/* Add Funds Button */}
                    {showAddFunds && (
                        <button
                            onClick={showAddFunds}
                            className="w-full mt-3 bg-white border border-green-300 text-green-700 py-2 px-4 rounded-lg font-medium hover:bg-green-50 flex items-center justify-center space-x-2 transition-colors"
                        >
                            <Plus className="w-4 h-4" />
                            <span>Add Funds</span>
                        </button>
                    )}
                </div>

                {/* Payment Summary */}
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <div className="flex justify-between items-center mb-2">
                        <span className="text-gray-700">Payment Amount:</span>
                        <span className="font-medium text-gray-900">{formatAmount(paymentAmount)}</span>
                    </div>
                    <div className="flex justify-between items-center">
                        <span className="text-gray-700">After Payment:</span>
                        <span className={`font-medium ${insufficientBalance ? 'text-red-600' : 'text-green-600'}`}>
                            {insufficientBalance ? 'Insufficient funds' : formatAmount(walletBalance - paymentAmount)}
                        </span>
                    </div>
                </div>

                {/* Error Message */}
                {error && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                        <p className="text-red-800 text-sm">{error}</p>
                    </div>
                )}

                {/* Insufficient Balance Warning */}
                {insufficientBalance && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                        <h4 className="font-medium text-yellow-900 mb-2">Insufficient Balance</h4>
                        <p className="text-yellow-700 text-sm">
                            You need {formatAmount(paymentAmount - walletBalance)} more in your wallet to complete this payment.
                            {showAddFunds ? ' Please add funds to continue.' : ''}
                        </p>
                    </div>
                )}
                {currencyMismatch && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <h4 className="font-medium text-blue-900 mb-2">Currency Mismatch</h4>
                        <p className="text-blue-700 text-sm">
                            Your wallet is denominated in {walletCurrency || 'N/A'}, but this transaction requires{' '}
                            {normalizedGatewayCurrency || 'N/A'}. Switch to the matching country or fund the correct wallet to continue.
                        </p>
                    </div>
                )}

                {/* Payment Button */}
                <button
                    onClick={handlePayClick}
                    disabled={loading || insufficientBalance || !!currencyMismatch}
                    className={`w-full py-3 px-4 rounded-lg font-medium transition-colors flex items-center justify-center space-x-2 ${insufficientBalance || loading || !!currencyMismatch
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        : 'bg-green-600 text-white hover:bg-green-700'
                        }`}
                >
                    {loading ? (
                        <>
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                            <span>Processing...</span>
                        </>
                    ) : (
                        <>
                            <Wallet className="w-4 h-4" />
                            <span>Pay with Wallet</span>
                        </>
                    )}
                </button>
            </div>
        </>
    );
};

export default WalletPayment;
